/**
 * O MODELO do relatório comparativo (lote de 09/10/2026, item 7) — puro, sem DOM.
 *
 * Pedido literal: "Em cenários, crie um botão que me permite gerar um relatório trazendo os
 * cenários que eu selecionar e uma análise crítica comparativa deles."
 *
 * O QUE ENTRA: cada cenário na última versão PUBLICADA (`abrirCenario` → `{empreendimento,
 * lev}`), nunca o rascunho e nunca a lista em memória do simulador (`estado.cenarios` tem
 * cenários fantasmas: o "Otimizado" do otimizador só existe na tela).
 *
 * O NÚMERO É O DA TELA DE CADA CENÁRIO, e isso exige reproduzir a DERIVAÇÃO que a tela faz
 * antes de calcular: com a tabela "Demanda por unidade" preenchida, `aplicarTravasDemanda`
 * (simulador.js) grava no modelo as unidades (= linhas da tabela), o VGV, as permutadas, as
 * vendidas e as financiadas que saem da tabela — e o motor lê esses campos como entrada.
 * O cadastro é de CADA cenário desde a 0032 (antes era do empreendimento, compartilhado), e a
 * derivação continua sendo feita sobre uma CÓPIA, cenário a cenário: o objeto pode ser o vivo
 * da tela, e o VGV de um cenário com tabela é o da tabela DELE. `derivarCenario` é a única implementação dessa
 * projeção fora do legado, e a Comparação da tela (renderCmp) passa a usar esta mesma, pela
 * ponte `window.__fincastAnalise` — `tests/relatorio-paridade.test.ts` prova que ela é a
 * `aplicarTravasDemanda` da tela, avaliada do próprio simulador.js, nos 77 casos do golden.
 *
 * QUEM LIMITOU O PJ sai do MOTOR, recalculando sem cada trava — nunca de uma conta à mão
 * com o valor digitado (é a mesma régua de `pjLimInfo`, EL-192).
 */
import { calcEstatico, demTravas, ymOf } from '../../../../engine/index';
import type { Cor, Empreendimento, Lev, ResultadoEstatico } from '../../../../engine/index';
import { CRIT_NOMES, faltantesDoCenario, objetivoPorChave, veredito } from './textos';
import type { ChaveObjetivo, Objetivo, Veredito } from './textos';
import type { CampoValor } from './frase';

/* ============================================================================
 * 1. A derivação da tela, sobre uma cópia
 * ========================================================================== */

export interface EntradaCenario {
  empreendimento: Empreendimento;
  lev: Lev;
}

/**
 * A MESMA derivação de `aplicarTravasDemanda` (simulador.js), sem DOM e sem escrever nos
 * argumentos. Cada trava só vale quando a tabela tem o dado correspondente (`demTravas`):
 *   pB (a tabela tem dados)     → unidades = nº de linhas da tabela · permutadas derivadas
 *   pV (alguma avaliação > 0)   → VGV = Σ avaliações (unidades + vagas avulsas)
 *   pC (alguma venda)           → vendidas na demanda
 *   pI (alguma "ingressa")      → financiadas na demanda
 * Sem tabela, devolve cópias iguais à entrada — byte a byte o que a tela calcula.
 */
export function derivarCenario(b: Empreendimento, lev: Lev): EntradaCenario {
  const t = demTravas(lev);
  const linhas = Array.isArray(lev.unidadesDem) ? lev.unidadesDem.length : 0;
  const bb: Empreendimento = { ...b };
  const LL: Lev = { ...lev };
  if (t.pB) bb.unidades = linhas;
  if (t.pV) bb.VGV = t.derivados.vgv;
  if (t.pC) LL.unidVendDem = t.derivados.vend;
  if (t.pI) LL.unidFinDem = t.derivados.fin;
  if (t.pB) bb.permutadas = t.derivados.perm;
  return { empreendimento: bb, lev: LL };
}

/* ============================================================================
 * 2. Quem limitou o PJ — pelo motor
 * ========================================================================== */

export type TravaPJ = 'pjLimite' | 'lgc' | 'normativoVGV' | 'normativoCTO';

export interface LimitePJ {
  /**
   * 'piso' = as garantias já cobrem o CTO (o PJ é zero);
   * 'necessidade' = o PJ é exatamente o que falta para o CTO (nenhuma trava segurou);
   * 'trava' = uma (ou mais, empatadas) das travas segurou o PJ abaixo do que falta.
   */
  situacao: 'piso' | 'necessidade' | 'trava';
  travas: TravaPJ[];
  /** O PJ que o motor dá SEM o "PJ limite" (null: o campo está vazio). */
  semPjLimite: number | null;
  /** O PJ que o motor dá SEM a trava pela linha de crédito (null: "PJ limitado ao LGC" = Não). */
  semLGC: number | null;
}

/** A régua do motor para "há PJ limite" (`tetoInformadoPJ`): número finito > 0. */
function temPjLimite(b: Empreendimento): boolean {
  return typeof b.pjLimite === 'number' && Number.isFinite(b.pjLimite) && b.pjLimite > 0;
}

function semChavePjLimite(b: Empreendimento): Empreendimento {
  const c: Empreendimento = { ...b };
  delete c.pjLimite;
  return c;
}

export function quemLimitouPJ(b: Empreendimento, L: Lev, cE: ResultadoEstatico): LimitePJ {
  const TOL = 0.5;
  const need = cE.CTO - cE.garantiasTotal;
  const bSem = semChavePjLimite(b);
  const LSem: Lev = { ...L, pjLimitadoLGC: false };
  const semPjLimite = temPjLimite(b) ? calcEstatico(bSem, L).PJ : null;
  const semLGC = L.pjLimitadoLGC ? calcEstatico(b, LSem).PJ : null;
  const base = { semPjLimite, semLGC };
  if (need <= TOL) return { situacao: 'piso', travas: [], ...base };
  if (Math.abs(cE.PJ - need) < TOL) return { situacao: 'necessidade', travas: [], ...base };

  const travas: TravaPJ[] = [];
  if (semPjLimite !== null && semPjLimite > cE.PJ + TOL) travas.push('pjLimite');
  if (semLGC !== null && semLGC > cE.PJ + TOL) travas.push('lgc');
  /* Sem as duas travas que o usuário informa, sobra o teto normativo. Se mesmo assim o PJ
     fica abaixo do que falta E igual ao de agora, é ele (sozinho ou empatado) que segura. */
  const semInformadas = calcEstatico(bSem, LSem).PJ;
  if (semInformadas < need - TOL && Math.abs(semInformadas - cE.PJ) < TOL) {
    /* Qual metade do mínimo do motor (`tetoPJ = mín(60% VGV, 85% CTO)`) ficou com o valor —
       só se IDENTIFICA a metade, o número é o `tetoPJ` do motor. */
    if (Math.abs(cE.tetoPJ - 0.60 * b.VGV) < TOL) travas.push('normativoVGV');
    if (Math.abs(cE.tetoPJ - 0.85 * cE.CTO) < TOL) travas.push('normativoCTO');
  }
  /* Empate entre o "PJ limite" e a linha de crédito, abaixo do normativo: tirar uma sozinha
     não muda nada (a outra segura no mesmo valor). As duas seguram. */
  if (travas.length === 0 && semInformadas > cE.PJ + TOL) {
    if (semPjLimite !== null) travas.push('pjLimite');
    if (semLGC !== null) travas.push('lgc');
  }
  return { situacao: 'trava', travas, ...base };
}

/* ============================================================================
 * 3. Folgas — só onde elas existem
 * ========================================================================== */

export type NomeFolga = 'Vendidas' | 'Financiadas' | 'LGC' | 'IGH';

export interface Folgas {
  /** normalizadas pelo mínimo exógeno, como `guiaFolgas` (simulador.js) */
  vend: number;
  fin: number;
  lgc: number;
  igh: number;
  /** o critério mais apertado dos quatro — no empate, o primeiro (como `guiaFolgas`) */
  minimo: { nome: NomeFolga; v: number };
  /** obra: em PONTOS PERCENTUAIS acima do mínimo de obra */
  obraPP: number;
  /** data: MESES entre o "Cumprimento de demanda" e o prazo normativo (null sem uma das duas datas) */
  prazoMeses: number | null;
}

/**
 * A folga dos critérios, com a régua de `guiaFolgas` para os quatro normalizados (o teste de
 * paridade compara o critério mais apertado com o do Guiado). "Garantias ≥ CTO" NÃO tem
 * folga: o PJ fecha a conta, e a "folga" ficaria ≤ 0 travando o mínimo em zero (Catálogo de
 * objetivos, princípio do maximin).
 */
export function folgasDoCenario(b: Empreendimento, L: Lev, c: ResultadoEstatico): Folgas {
  const arr: Array<{ nome: NomeFolga; v: number }> = [
    { nome: 'Vendidas', v: c.vendExig > 0 ? ((L.unidVendDem || 0) - c.vendExig) / c.vendExig : 1 },
    { nome: 'Financiadas', v: c.finExig > 0 ? ((L.unidFinDem || 0) - c.finExig) / c.finExig : 1 },
    { nome: 'LGC', v: (b.lgcDisponivel || 0) > 0 ? c.saldoLGC / b.lgcDisponivel : 1 },
    { nome: 'IGH', v: c.ighMin > 0 ? (c.ighReal - c.ighMin) / c.ighMin : 1 },
  ];
  const minimo = arr.reduce((a, x) => (x.v < a.v ? x : a));
  return {
    vend: arr[0]!.v, fin: arr[1]!.v, lgc: arr[2]!.v, igh: arr[3]!.v,
    minimo,
    obraPP: (L.obraGarPct || 0) - (L.minObraPct || 0),
    prazoMeses: c.ymDesejada !== null && c.ymNorm !== null ? c.ymNorm - c.ymDesejada : null,
  };
}

/* ============================================================================
 * 4. O modelo
 * ========================================================================== */

export interface CenarioParaRelatorio {
  id: string;
  nome: string;
  versaoPublicada: number;
  empresaId: string;
  empreendimentoId: string;
  /** o PUBLICADO, como `abrirCenario` devolve */
  entrada: EntradaCenario;
  /** a pessoa que gerou tem alteração não publicada neste cenário */
  temRascunhoMeu: boolean;
}

export interface ContextoRelatorio {
  incorporadora: string;
  empreendimento: string;
  geradoPor: string;
  /** data e hora absolutas, já escritas ("09/10/2026 às 14:32") — o módulo não lê relógio */
  geradoEm: string;
  objetivo: ChaveObjetivo;
}

export interface Criterio {
  nome: string;
  ok: boolean;
}

export interface Premissas {
  temTabela: boolean;
  programa: string;
  fundingObra: string;
  temEVV: boolean;
  terrenoGarantia: boolean;
  pjLimitadoLGC: boolean;
}

export interface ModeloCenario {
  id: string;
  nome: string;
  versaoPublicada: number;
  temRascunhoMeu: boolean;
  /** `false` = faltam campos obrigatórios: entra marcado e fica FORA do ranking */
  suficiente: boolean;
  faltam: string[];
  cor: Cor;
  veredito: Veredito;
  criterios: Criterio[];
  limitePJ: LimitePJ;
  /** o critério de folga mais apertado (a régua do objetivo "Máxima folga") */
  folgaMaisApertada: NomeFolga;
  /** alavancagem sem denominador: "ilimitada" quando há PJ e nenhum recurso próprio */
  semRecursosProprios: boolean;
  premissas: Premissas;
  /** todo número do cenário que o relatório mostra ou cita — `null` = não se aplica */
  valores: Record<CampoValor, number | null>;
  /** as travas do Guiado gravadas no cenário (escondem alavancas) */
  travadas: string[];
}

export interface ModeloRelatorio {
  incorporadora: string;
  empreendimento: string;
  geradoPor: string;
  geradoEm: string;
  objetivo: Objetivo;
  cenarios: ModeloCenario[];
}

export function modeloDoCenario(c: CenarioParaRelatorio): ModeloCenario {
  const { empreendimento: b, lev: L } = derivarCenario(c.entrada.empreendimento, c.entrada.lev);
  const cE = calcEstatico(b, L);
  const faltam = faltantesDoCenario(b, L);
  const suficiente = faltam.length === 0;
  const oks = [cE.p.hGar, cE.p.hVend, cE.p.hRep, cE.p.hLGC, cE.p.hIGH, cE.p.sObra, !cE.dataLimiteExcede];
  const fol = folgasDoCenario(b, L, cE);
  const lim = quemLimitouPJ(b, L, cE);
  const recursos = cE.aporte + cE.valorTerrenoGar;
  const semRecursos = recursos <= 0.5;
  const total = cE.garantiasTotal + cE.PJ;
  const valores: Record<CampoValor, number | null> = {
    versao: c.versaoPublicada,
    VGV: b.VGV || 0,
    unidades: b.unidades || 0,
    permutadas: b.permutadas || 0,
    CTE: cE.CTE,
    CTO: cE.CTO,
    valorTerreno: b.valorTerreno || 0,
    FSF: cE.valorFinDem,
    terreno: cE.valorTerrenoGar,
    obra: cE.valorObra,
    aporte: cE.aporte,
    garantias: cE.garantiasTotal,
    PJ: cE.PJ,
    total,
    gapCTO: cE.gapCTO,
    cobertura: cE.CTO > 0 ? total / cE.CTO : null,
    tetoPJ: cE.tetoPJ,
    pjLimite: temPjLimite(b) ? (b.pjLimite as number) : null,
    pjSemLimite: lim.semPjLimite,
    pjSemLGC: lim.semLGC,
    lgcDisponivel: b.lgcDisponivel || 0,
    lgcPct: L.lgcPct || 0,
    tomadaLGC: cE.tomadaLGC,
    saldoLGC: cE.saldoLGC,
    ighReal: cE.ighReal,
    ighMin: cE.ighMin,
    vendidas: L.unidVendDem || 0,
    vendExig: cE.vendExig,
    salesMin: cE.salesMin,
    financiadas: L.unidFinDem || 0,
    finExig: cE.finExig,
    finMin: cE.finMin,
    unidadesExigidas: cE.salesMin + cE.finMin,
    pctFin: L.pctFin || 0,
    obraGarPct: L.obraGarPct || 0,
    minObraPct: L.minObraPct || 0,
    ymDesejada: cE.ymDesejada,
    ymNorm: cE.ymNorm,
    ymContratacao: ymOf(L.dataContratacao),
    recursosProprios: recursos,
    alavancagem: semRecursos ? null : cE.PJ / recursos,
    resultado: (b.VGV || 0) - cE.CTE,
    folgaVend: fol.vend,
    folgaFin: fol.fin,
    folgaLGC: fol.lgc,
    folgaIGH: fol.igh,
    folgaMin: fol.minimo.v,
    folgaObra: fol.obraPP,
    folgaPrazo: fol.prazoMeses,
    nCriterios: oks.filter(Boolean).length,
  };
  return {
    id: c.id,
    nome: c.nome,
    versaoPublicada: c.versaoPublicada,
    temRascunhoMeu: c.temRascunhoMeu,
    suficiente,
    faltam,
    cor: cE.color,
    veredito: suficiente ? veredito(cE.color) : veredito(null),
    criterios: CRIT_NOMES.map((nome, i) => ({ nome, ok: oks[i]! })),
    limitePJ: lim,
    folgaMaisApertada: fol.minimo.nome,
    semRecursosProprios: semRecursos,
    premissas: {
      temTabela: demTravas(L).pB,
      programa: b.programa,
      fundingObra: L.fundingObra ?? '—',
      temEVV: !!L.temEVV,
      terrenoGarantia: !!L.terrenoGarantia,
      pjLimitadoLGC: !!L.pjLimitadoLGC,
    },
    valores,
    travadas: Array.isArray(L.travadas) ? [...L.travadas] : [],
  };
}

/** Qual folga é a mais apertada, pelo nome do critério do veredito (para a frase). */
export function nomeDaFolga(n: NomeFolga): string {
  return n === 'Vendidas' ? 'Vendidas ≥ mín' : n === 'Financiadas' ? 'Financiadas ≥ mín' : n === 'LGC' ? 'LGC Disponível' : 'IGH ≥ mínimo';
}

export function montarModelo(ctx: ContextoRelatorio, cenarios: readonly CenarioParaRelatorio[]): ModeloRelatorio {
  const objetivo = objetivoPorChave(ctx.objetivo);
  if (objetivo === null) throw new Error('Objetivo do relatório desconhecido.');
  return {
    incorporadora: ctx.incorporadora,
    empreendimento: ctx.empreendimento,
    geradoPor: ctx.geradoPor,
    geradoEm: ctx.geradoEm,
    objetivo,
    cenarios: cenarios.map(modeloDoCenario),
  };
}
